import _ from 'lodash';
import { addTruthyParamsToUrl } from '@zapier/url-utils';
import { getCookie, isBrowser } from '@zapier/common-utils';
import NetworkError, { ErrorDetails } from './NetworkError';

const fetchSuccess = function (response: Response & { responseText?: string }) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  return response.text().then((body: string) => {
    // Hack to make legacy z.useful.errors work for now.
    response.responseText = body;
    throw response;
  });
};

// If the client doesn't already have a CSRF token, then retrieve one
const retrieveCsrfToken = (crossOrigin: Boolean) => {
  if (crossOrigin) {
    return (
      fetch(`${process.env.ZAPIER_API_BASE_URL}/api/v3/csrf`, {
        credentials: 'include',
        mode: 'cors',
      })
        .then(fetchSuccess)
        // The `fetch` call can reject with a `TypeError` or `AbortError`, but `fetchSuccess`
        // can reject with the actual response instead of an error (*flips table*). It's too
        // late to change this because of all the downstream dependencies on this behavior, so
        // we're going to have to live with it.
        .catch(errorOrResponse => {
          throw new NetworkError({
            ...errorOrResponse,
            message: 'Unable to retrieve CSRF token.',
          });
        })
    );
  }
  return getCookie('csrftoken')
    ? Promise.resolve()
    : fetch('/api/v3/csrf', { credentials: 'same-origin' })
        .then(fetchSuccess)
        // The `fetch` call can reject with a `TypeError` or `AbortError`, but `fetchSuccess`
        // can reject with the actual response instead of an error (*flips table*). It's too
        // late to change this because of all the downstream dependencies on this behavior, so
        // we're going to have to live with it.
        .catch(errorOrResponse => {
          throw new NetworkError({
            ...errorOrResponse,
            message: 'Unable to retrieve CSRF token.',
          });
        });
};

/**
 * The CSRF token needs to be the first thing retrieved before any CSRF-protected
 * request can take place (e.g. POST requests), so all requests should wait until
 * that resolves before proceeding.
 * To improve performance, we cache the promise from the first call to `csrfTokenReady`.
 */
let _retrieveCsrfTokenPromise: any = null;
export function csrfTokenReady(crossOrigin: Boolean) {
  if (_retrieveCsrfTokenPromise) {
    return _retrieveCsrfTokenPromise;
  }

  _retrieveCsrfTokenPromise = retrieveCsrfToken(crossOrigin);
  return _retrieveCsrfTokenPromise;
}

/**
 * Retry trying to get `document.location` if it's not available immediately.
 * We do that because, weirdly, we're seeing some errors in Sentry where `document.location` is `null`.
 * After some initial investigation, it looks like Firefox has a bug where `document.location`
 * is not being properly set until a couple of seconds after the page is loaded.
 */
const waitForLocation = function (
  totalTimeWaited: number = 0
): Promise<Location> {
  if (document.location) {
    return Promise.resolve(document.location);
  }

  if (totalTimeWaited > 2000) {
    return Promise.reject("Couldn't find document.location after 2 seconds.");
  }

  return new Promise(resolve => {
    setTimeout(() => {
      waitForLocation(totalTimeWaited + 100).then(resolve);
    }, 100);
  });
};

/**
 * Test that the base of the request url matches the Zapier API Base URL.
 */
const urlMatchesZapierApiBaseUrl = function (url: string): Boolean {
  if (!process.env.ZAPIER_API_BASE_URL) {
    return false;
  }

  const parsedBaseUrl = new URL(process.env.ZAPIER_API_BASE_URL);

  try {
    const parsedUrl = new URL(url);

    return parsedUrl.origin === parsedBaseUrl.origin;
  } catch (err) {
    // the url could have been protocol-relative or relative
    return false;
  }
};

/**
 * Test that a given url is a same-origin URL.
 * url could be relative or scheme relative or absolute host + port.
 */
const sameOrigin = function (url: string) {
  return waitForLocation().then((location: Location) => {
    const host = location.host;
    const protocol = location.protocol;
    const sr_origin = '//' + host;
    const origin = protocol + sr_origin;

    /* Allow absolute or scheme relative URLs to same origin
     * or any other URL that isn't scheme relative or absolute i.e relative.
     */
    return (
      url === origin ||
      url.slice(0, origin.length + 1) === origin + '/' ||
      url === sr_origin ||
      url.slice(0, sr_origin.length + 1) === sr_origin + '/' ||
      !/^(\/\/|http:|https:).*/.test(url)
    );
  });
};

function isRelative(path: string) {
  return (
    path.indexOf('//') !== 0 &&
    path.indexOf('https://') !== 0 &&
    path.indexOf('http://') !== 0
  );
}

/**
 * Checks if the request about to be fired needs an CSRF token.
 * If it does, fetch the token and set it as a request header.
 */
async function setCsrfHeaderIfNeeded(
  url: string,
  info: RequestInit
): Promise<RequestInit> {
  const method = info.method || '';
  const isCsrfSafeMethod = /^(GET|HEAD|OPTIONS|TRACE)$/.test(method);

  // CSRF-safe methods don't need the CSRF header.
  if (isCsrfSafeMethod) {
    return info;
  }

  // We currently don't support CSRF for non-browser environments.
  if (!isBrowser()) {
    return info;
  }

  // We don't support cross-origin CSRF, except requests from Zapier subdomains to valid
  // Zapier URLs.
  const isSameOrigin = await sameOrigin(url);
  const isSafeCrossOrigin = urlMatchesZapierApiBaseUrl(url);
  if (!isSameOrigin && !isSafeCrossOrigin) {
    return info;
  }

  const resp = await csrfTokenReady(isSafeCrossOrigin);
  const token = isSafeCrossOrigin
    ? resp.headers.get('x-upstreamcsrftoken')
    : getCookie('csrftoken');

  if (token) {
    info.headers = new Headers(info.headers || {});
    info.headers.set('X-CSRFToken', token);
  }

  return info;
}

let onFetchCompleteListener = (_response: Response) => {};
// Allow consumers to listen to completed fetch requests.
// This is nasty, but needed to preserve backwards-compatibility with the fetchMiddleware from `@zapier/toolbox-redux-framework`.
export function onFetchComplete(fn: typeof onFetchCompleteListener) {
  onFetchCompleteListener = fn;
}

// Note that we don't support passing in `url` `Request` objects, only strings.
const wrappedFetch = function (url: string, info: RequestInit = {}) {
  info = { ...info };
  info.headers = new Headers(info.headers || {});
  info.headers.set('X-Requested-With', 'XMLHttpRequest'); // help django's request.is_ajax()
  info.credentials = 'include';

  // For relative URLs, we let the downstream consumer of the library customize the base URL to prepend
  // via the ZAPIER_API_BASE_URL environment variable.
  const apiUrl = isRelative(url)
    ? `${process.env.ZAPIER_API_BASE_URL || ''}${url}`
    : url;

  return setCsrfHeaderIfNeeded(apiUrl, info).then(newInfo => {
    return (
      fetch(apiUrl, newInfo)
        .then(response => {
          onFetchCompleteListener(response);
          return response;
        })
        // Our `fetch` call can reject with a `TypeError` or `AbortError`, but `setCsrfHeaderIfNeeded`
        // can reject with the actual response instead of an error (*flips table*). It's too late to
        // change this because of all the downstream dependencies on this behavior, so we're going to
        // have to live with it.
        .catch((errorOrResponse: ErrorDetails) => {
          // Sentry captures this error:
          //
          // > We're having some trouble connecting. You might try reloading the page
          // > or checking your internet connection. Only absolute URLs are supported
          //
          // Let's try to include the URL so we can better understand the issue
          const originalMessage = errorOrResponse.message || '';
          const newMessage = `${originalMessage} url='${apiUrl}'`;
          throw new NetworkError({ ...errorOrResponse, message: newMessage });
        })
    );
  });
};

wrappedFetch.success = fetchSuccess;

wrappedFetch.json = function (response: any) {
  return response
    .json()
    .then((data: any) => {
      return data;
    })
    .catch((err: any) => {
      // Empty content is expected with a 204 and is valid,
      // though parsing it as JSON would fail.
      if (response.status === 204) {
        return null;
      }
      // Throw the error if it was a problem parsing.
      if (response.status >= 200 && response.status < 300) {
        throw err;
      }
      // Otherwise, maybe this was an error status and JSON wasn't expected.
      throw response;
    });
};

// FIXME: This should be a named export instead.
wrappedFetch.url = function (url: string, params: { [key: string]: string }) {
  if (!params) {
    return url;
  }
  const queryString = Object.keys(params).reduce(
    (prevQueryString, paramKey) => {
      const value = params[paramKey];
      let nextQueryString = prevQueryString;
      if (typeof value !== 'undefined' && value !== null) {
        if (nextQueryString) {
          nextQueryString += '&';
        }
        nextQueryString += `${encodeURIComponent(
          paramKey
        )}=${encodeURIComponent(value)}`;
      }
      return nextQueryString;
    },
    ''
  );
  return url + (queryString ? '?' : '') + queryString;
};

export const fetchJson = (uri: string, info?: RequestInit) => {
  info = info ? _.extend({}, info) : {};
  info.headers = new Headers(info.headers || {});
  info.headers.set('Content-Type', 'application/json');
  info.headers.set('Accept', 'application/json');

  return wrappedFetch(uri, info)
    .then(wrappedFetch.success)
    .then(wrappedFetch.json);
};

// `fetchJson` via `method`, passing along a stringified
// `body` if it exists.
const fetchJsonWithMethod = (method: string, url: string, body?: {}) =>
  fetchJson(url, {
    ...(method !== 'GET' ? { body: body && JSON.stringify(body) } : {}),
    method,
  });

export const deleteJson = _.partial(fetchJsonWithMethod, 'DELETE');
export const getJson = _.partial(fetchJsonWithMethod, 'GET');
export const patchJson = _.partial(fetchJsonWithMethod, 'PATCH');
export const postJson = _.partial(fetchJsonWithMethod, 'POST');
export const putJson = _.partial(fetchJsonWithMethod, 'PUT');

export const postJsonWithAccountId = (
  accountId: string,
  url: string,
  body: {} = {}
) => postJson(url, { ...body, account_id: accountId });

export const sendFormData = (
  url: string,
  formData: FormData,
  opts: RequestInit = {}
) => {
  if (opts.headers) {
    opts.headers = new Headers(opts.headers || {});
    opts.headers.set('Accept', 'application/json');
  }

  const options = {
    method: 'POST',
    ...opts,
    body: formData,
  };
  return wrappedFetch(url, options)
    .then(wrappedFetch.success)
    .then(wrappedFetch.json);
};

const isResponseOfType = (type: string, response: Response) =>
  _.isFunction(_.get(response, ['headers', 'get'])) &&
  response.headers.get('content-type')!.indexOf(type) > -1;
export const isJsonResponse = _.partial(isResponseOfType, 'application/json');

export const getJsonWithAccountId = (accountId: string, url: string) =>
  getJson(addTruthyParamsToUrl(url, { account_id: accountId }));

export default wrappedFetch;
